import { type JanusCtx, type Row, activeStatuses, definitionNotHeld, handBack, record, upper } from '../context.js';
import { layerListEntryUuid } from '../statuses/status-trigger-gate.js';
import { heldLayerlessDefinition } from '../../compile/held-definition.js';

/** The pre-draw status guard of the resource-gain rolls (increase_mana_roll / increase_luck_roll / temp_v_roll — playtest 2026-10-14: Wizard
 *  Guy's Gravitational Reserve refused `status-modifiers-not-verified` on every kill because the guard refused ANY status, and every PC holds
 *  Awake). PHP reads a held status in these rolls only through its roll-type layers, each listing the roll types it touches
 *  (janus_roll_type_adv_dis_delta_for_resolved_roll's caster sources + the target's against axes, fire_caster_makes_roll_type_trigger,
 *  process_status_effects_next_roll_type_modifiers' extremes / halve / double and its triggers_on_next_roll_type use-up). None is modelled
 *  here, so a status — or a passive it attaches — whose roll-type layer lists THIS roll type hands back by name; every other status is inert. */
export function rollTypeStatusGuard(ctx: JanusCtx, tag: string, rollType: string, rows: readonly Row[]): void {
  const type = upper(rollType);
  /** A definition the page HOLDS: layers, or a held layer-less row (heldLayerlessDefinition). A bare reference shell ({items:{item_id,
   *  item_name, item_type}}) is unknown — reading it as layer-less would silently skip a status that names this roll type. */
  const held = (def: Row | null): def is Row => !!def && (!!record(def.item_layer_types) || heldLayerlessDefinition(def));
  const touches = (def: Row): string | null => {
    for (const key of Object.keys(record(def.item_layer_types) ?? {}))
      if (key !== 'roll_type' && key.includes('roll_type')
          && ctx.read.entries(def, key).some((entry) => layerListEntryUuid(entry) === type)) return key;
    return null;
  };
  for (const row of new Set(rows))
    for (const status of activeStatuses(ctx, row)) {
      const inline = record(status.status_arr);
      const def = inline && record(inline.item_layer_types) ? inline : ctx.read.definition(upper(status.status_item_id));
      if (!held(def)) definitionNotHeld(`${tag}:status-definition-not-held`, status.status_item_id);
      const own = touches(def!);
      if (own) handBack(`${tag}:unmodelled:status.${own}`);
      for (const entry of ctx.read.entries(def!, 'passive_ability')) {
        const id = layerListEntryUuid(entry);
        const passive = ctx.read.definition(id);
        if (!held(passive)) definitionNotHeld(`${tag}:status-passive-definition-not-held`, id);
        const attached = touches(passive!);
        if (attached) handBack(`${tag}:unmodelled:status-passive.${attached}`);
      }
    }
}
